/** 운영 지표 화면의 순수 계산. 렌더와 떼어 놓아야 테스트가 붙는다. */
import type { AdminHeatCell, AdminWorkspaceRow } from "@/lib/api/types";

/** 서버의 weekday 0=월 … 6=일 (KST) 과 같은 순서. */
export const WEEKDAYS = ["월", "화", "수", "목", "금", "토", "일"];
export const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** 168 개 셀을 7×24 격자로 편다. 빠진 칸은 0 — 서버가 항상 채워 주지만
 *  한 칸이라도 비면 `undefined` 가 화면에 찍히므로 여기서 막는다. */
export function heatmapGrid(cells: AdminHeatCell[]): number[][] {
  const grid = WEEKDAYS.map(() => HOURS.map(() => 0));
  for (const cell of cells) {
    const row = grid[cell.weekday];
    if (row && cell.hour >= 0 && cell.hour < 24) row[cell.hour] = cell.count;
  }
  return grid;
}

/** 히트맵 셀 배경의 불투명도. 상한 0.55 를 넘기지 않는다 — 그 위로 가면
 *  셀 위에 찍는 --label-normal 숫자가 대비 미달이 된다. 색은 보조 채널이다. */
export function heatOpacity(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return 0.08 + (value / max) * 0.47;
}

/** 막대 높이. 1 건짜리 날이 0px 로 사라지지 않게 하한 3% 를 준다. */
export function barHeight(value: number, max: number): string {
  if (value <= 0) return "0%";
  return `max(3%, ${Math.min(100, (value / Math.max(max, 1)) * 100)}%)`;
}

/** 막대 폭(%) — 분모가 0 이어도 NaN 을 내지 않는다. */
export function barWidth(value: number, base: number): number {
  if (base <= 0 || value <= 0) return 0;
  return Math.min(100, (value / base) * 100);
}

/** 서버가 이미 0~100 으로 준 값이라 100 을 곱하지 않는다. */
export function pct(value: number): string {
  return `${value.toFixed(1)}%`;
}

export function count(value: number): string {
  return value.toLocaleString("ko-KR");
}

/** 워크스페이스 생성 → 첫 주문까지 걸린 시간. 하루를 넘으면 일로 읽는 게 낫다. */
export function formatHours(hours: number | null): string {
  if (hours === null) return "-";
  if (hours < 24) return `${hours.toFixed(1)}시간`;
  return `${(hours / 24).toFixed(1)}일`;
}

/** YYYY-MM-DD → M/D. 축 라벨은 짧을수록 좋다. */
export function shortDate(date: string): string {
  const [, month, day] = date.split("-");
  return month && day ? `${Number(month)}/${Number(day)}` : date;
}

export type SortKey =
  | "name"
  | "owner_name"
  | "member_count"
  | "people_active"
  | "orders_total"
  | "orders_recent"
  | "dormant_days"
  | "fill_rate"
  | "close_rate"
  | "custom_menu_rate"
  | "seed_ratio"
  | "first_order_hours";

export type SortDir = "asc" | "desc";

export function sortRows(
  rows: AdminWorkspaceRow[],
  key: SortKey,
  dir: SortDir
): AdminWorkspaceRow[] {
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    // 주문이 한 번도 없는 행의 dormant_days 는 "마지막 주문 이후"가 아니라 워크스페이스
    // 생성 이후 경과일이라 서로 다른 숫자다. 200일 묵은 빈 워크스페이스가 진짜로 식은
    // 활성 워크스페이스를 밀어내지 않게, 이 열에서는 방향과 무관하게 아래로 보낸다.
    if (key === "dormant_days" && a.ever_ordered !== b.ever_ordered) {
      return a.ever_ordered ? -1 : 1;
    }
    const av = a[key];
    const bv = b[key];
    // 값이 없는 행(첫 주문 미도달)은 방향과 무관하게 항상 아래로 보낸다.
    // 오름차순마다 활성화 못한 워크스페이스가 표 머리를 차지하면 쓸모가 없다.
    if (av === null || bv === null) return av === bv ? 0 : av === null ? 1 : -1;
    if (typeof av === "string" && typeof bv === "string") {
      return sign * av.localeCompare(bv, "ko");
    }
    return sign * (Number(av) - Number(bv));
  });
}
